import { memo } from 'react'
import { Box, Card, Link } from '@mui/material'
import Autoplay from 'embla-carousel-autoplay'
import { NavLink } from 'react-router-dom'

import { Image } from '../shared/image'
import Text from '../shared/text/Text'
import {
  Carousel,
  CarouselArrowBasicButtons,
  CarouselDotButtons,
  useCarousel
} from '../shared/carousel'
import { PLAY_GAMES_URL, POST_TO_EARN_URL } from '../../constants/navigation'
import { NAVIGATION_BLANK_PARAMS } from '../../util/navigation.helpers'
import { buildSleapUrl } from '../../util/game.helpers'

const AppFeatured = ({ t, sx, authId, onOpenCOEStore, ...other }) => {
  const carousel = useCarousel({ loop: true }, [
    Autoplay({ playOnInit: true, delay: 8000 })
  ])
  const list = [
    {
      id: 'travel-earn',
      heading: t('home.featured.new-earn-option'),
      title: t('home.featured.travel-earn-title'),
      desc: t('home.featured.travel-earn-desc'),
      to: buildSleapUrl(authId),
      external: true
    },
    {
      id: 'play-games',
      heading: t('home.featured.new-earn-option'),
      title: t('home.featured.play-games-title'),
      desc: t('home.featured.play-games-desc'),
      to: PLAY_GAMES_URL
    },
    {
      id: 'post-earn',
      heading: t('home.featured.new-earn-option'),
      title: t('home.featured.new-earn-option-title'),
      desc: t('home.featured.new-earn-option-desc'),
      to: POST_TO_EARN_URL
    },
    {
      id: 'vip-store',
      heading: t('home.featured.new-spend-option'),
      title: t('home.featured.new-spend-option-title'),
      desc: t('home.featured.new-spend-option-desc'),
      onClick: onOpenCOEStore
    }
  ]

  return (
    <Card
      sx={[
        { bgcolor: 'common.black', height: 1 },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      {...other}
    >
      <CarouselDotButtons
        scrollSnaps={carousel.dots.scrollSnaps}
        selectedIndex={carousel.dots.selectedIndex}
        onClickDot={carousel.dots.onClickDot}
        sx={{
          top: 16,
          left: 16,
          position: 'absolute',
          color: 'primary.light'
        }}
      />

      <CarouselArrowBasicButtons
        {...carousel.arrows}
        options={carousel.options}
        sx={{
          top: 8,
          right: 8,
          position: 'absolute',
          color: 'common.white'
        }}
      />

      <Carousel carousel={carousel}>
        {list.map((item) => (
          <CarouselItem key={item.id} item={item} />
        ))}
      </Carousel>
    </Card>
  )
}

function CarouselItem({
  item: { heading, title, desc, id, to, external, onClick },
  sx,
  ...other
}) {
  return (
    <Box
      sx={[
        {
          width: 1,
          position: 'relative',
          ...(onClick && { cursor: 'pointer' })
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      onClick={onClick}
      {...other}
      {...(to
        ? external
          ? { href: to, component: Link, ...NAVIGATION_BLANK_PARAMS }
          : { component: NavLink, to }
        : {})}
    >
      <Box
        sx={{
          p: 3,
          gap: 1,
          width: 1,
          bottom: 0,
          zIndex: 9,
          display: 'flex',
          position: 'absolute',
          color: 'common.white',
          flexDirection: 'column'
        }}
      >
        <Text
          variant='overline'
          sx={{ color: 'primary.light' }}
          label={heading}
        />
        <Text color='inherit' underline='none' variant='h5' label={title} />
        <Text variant='body2' label={desc} />
      </Box>

      <Image
        alt={title}
        src={`/assets/featured/${id}.jpg`}
        slotProps={{
          overlay: {
            sx: (theme) => ({
              backgroundImage: `linear-gradient(to bottom, transparent 0%, ${theme.vars.palette.common.black} 75%)`
            })
          }
        }}
        sx={{ width: 1, height: { xs: 288, xl: 320 } }}
      />
    </Box>
  )
}

export default memo(AppFeatured)
